import { Controller } from "@hotwired/stimulus"

// One-card pager for the home insight sections. Where paging applies the track
// (.home-widget-stack) is a nowrap flex row inside an overflow-hidden viewport;
// this controller slides it one card at a time with a CSS transform, driven by
// the footer's < > buttons and by an autoplay ring that advances on its own. A
// transform (rather than native scroll) keeps it deterministic across browsers.
// Where paging does not apply the track is a normal grid, so the controller
// clears the transform and hides the whole footer.
export default class extends Controller {
    static targets = ["track", "prev", "next", "controls", "autoplay", "sweep"]
    // fitHeight: collapse the track to the height of the card on screen. Pin chips
    // are all one height so they don't need it, but widget cards are not (a
    // Weather card is half a three-story Wire), and a flex row is otherwise as
    // tall as its tallest item — which would leave dead space under the short ones.
    static values = { fitHeight: Boolean }

    connect() {
        this.index = 0
        // Autoplay pauses for two independent reasons and runs only when
        // neither holds. A single boolean would let a mouse leaving the strip
        // cancel the pause the user asked for by pressing the ring.
        this.pauses = new Set()
        this._onResize = () => this._render()
        window.addEventListener("resize", this._onResize)

        // An autoplay that races on in a background tab drops the reader back
        // onto a card three past the one they left, which is worse than no
        // autoplay at all. Tab visibility is read live in _syncAutoplay rather
        // than latched here: a page that loads while hidden gets no
        // visibilitychange when it is revealed, and a latched flag would leave
        // the ring frozen for good.
        this._onVisibility = () => this._syncAutoplay()
        document.addEventListener("visibilitychange", this._onVisibility)

        // Hover pause, but only where hovering is a real gesture. On a touch
        // screen mouseenter fires on tap and the matching mouseleave often never
        // does, which would strand the ring paused after the first tap on a card.
        if (window.matchMedia("(hover: hover)").matches) {
            this._onEnter = () => this._setPause("hover", true)
            this._onLeave = () => this._setPause("hover", false)
            this.element.addEventListener("mouseenter", this._onEnter)
            this.element.addEventListener("mouseleave", this._onLeave)
        }

        // The ring's CSS sweep IS the clock: one lap is one card's dwell, so the
        // advance hangs off the lap boundary rather than a setInterval that would
        // drift out of step with what is drawn. That also means every way the
        // sweep can stop stops the advance for free — animation-play-state on a
        // pause, display:none on a strip that isn't paging, and the
        // prefers-reduced-motion rule that hides the ring outright.
        this._onLap = () => this._advance()
        if (this.hasSweepTarget) this.sweepTarget.addEventListener("animationiteration", this._onLap)

        // Cards come and go after connect - a pin's × removes one, and widget
        // cards render into lazily loaded frames - so the page count and the
        // active card's height have to be recomputed rather than captured once.
        this._mutations = new MutationObserver(() => this._render())
        this._sizes = new ResizeObserver(() => this._fit())
        if (this.hasTrackTarget) this._mutations.observe(this.trackTarget, { childList: true })
        this._render()
    }

    disconnect() {
        window.removeEventListener("resize", this._onResize)
        document.removeEventListener("visibilitychange", this._onVisibility)
        if (this._onEnter) this.element.removeEventListener("mouseenter", this._onEnter)
        if (this._onLeave) this.element.removeEventListener("mouseleave", this._onLeave)
        if (this._onLap && this.hasSweepTarget) this.sweepTarget.removeEventListener("animationiteration", this._onLap)
        this._mutations?.disconnect()
        this._sizes?.disconnect()
    }

    prev() {
        this.index = Math.max(0, this.index - 1)
        this._restartSweep()
        this._render()
    }

    next() {
        this.index = Math.min(this._lastIndex(), this.index + 1)
        this._restartSweep()
        this._render()
    }

    // The one pause that outlives the pointer leaving the strip. The label
    // tracks only this reason: hover and tab-visibility are states a screen
    // reader user never entered, so announcing them would describe a control
    // they cannot see changing under a press they did not make.
    toggleAutoplay() {
        this._setPause("manual", !this.pauses.has("manual"))
    }

    // Only children that actually take a slide count as pages. A contact chip and
    // a contact pin each render a sibling <template> holding their detail panel,
    // and flex layout skips a display:none child entirely - counting those let the
    // pager advance past the last real card onto empty slots.
    _cards() {
        if (!this.hasTrackTarget) return []

        return Array.from(this.trackTarget.children).filter((el) => el.tagName !== "TEMPLATE" && !el.hidden)
    }

    _lastIndex() {
        return Math.max(0, this._cards().length - 1)
    }

    // Paging is on for phones at any setting, and at every width for a home
    // whose owner asked for one card at a time (Dashboard#carousel_widgets,
    // which puts .assistant-home-panel--carousel on the panel). The CSS mixin
    // in _home_panel.scss is scoped by exactly the same two conditions — if
    // these two ever disagree the controls appear over an un-clipped grid, or
    // a clipped one gets no controls.
    _isPaged() {
        if (window.matchMedia("(max-width: 1023.98px)").matches) return true

        return !!this.element.closest(".assistant-home-panel--carousel")
    }

    // Autoplay's own step. Unlike next() it wraps, so an unattended strip loops
    // instead of parking on the last card forever. The < > still dead-end at the
    // edges — that is what makes their disabled state mean anything.
    _advance() {
        const count = this._cards().length
        if (!this._isPaged() || count < 2) return

        this.index = (this.index + 1) % count
        this._render()
    }

    _render() {
        if (!this.hasTrackTarget) return
        // The slide offset is a CSS var consumed only inside the mobile media
        // query, so a leftover index can never shift the desktop grid.
        const cards = this._cards()
        this.index = Math.min(this.index, Math.max(0, cards.length - 1))
        this.trackTarget.style.setProperty("--pager-offset", `-${this.index * 100}%`)

        const pageable = this._isPaged() && cards.length > 1
        if (this.hasControlsTarget) this.controlsTarget.classList.toggle("is-hidden", !pageable)
        if (this.hasPrevTarget) this.prevTarget.disabled = this.index <= 0
        if (this.hasNextTarget) this.nextTarget.disabled = this.index >= this._lastIndex()
        this._syncAutoplay()

        // Re-observe: a card that just arrived still has to report its height once
        // its frame fills in.
        if (this.fitHeightValue) {
            this._sizes.disconnect()
            cards.forEach((card) => this._sizes.observe(card))
        }
        this._fit()
        this._preload(cards)
    }

    // A paged card's turbo-frame carries loading="lazy", which only fetches
    // once the frame itself intersects the viewport. Two cards need help with
    // that: the active one, which is guaranteed to be on screen right now but
    // whose fetch otherwise still waits on the frame's own IntersectionObserver
    // callback to run - not instant, and in practice sometimes never fires at
    // all for a card that was off-canvas a moment ago; and the next one, which
    // is translated off-canvas and so never intersects until the very moment
    // it slides in. Bump both to "eager": the active card so what's already on
    // screen doesn't sit on a skeleton waiting on the observer, and the
    // upcoming one so its fetch is already running (or done) by the time < >
    // or autoplay reveals it. One card ahead only, not the whole stack - that
    // is what keeps a widget nobody pages to from ever costing a request.
    _preload(cards) {
        if (!this._isPaged()) return

        this._eager(cards[this.index])
        if (cards.length > 1) this._eager(cards[(this.index + 1) % cards.length])
    }

    _eager(card) {
        const frame = card?.querySelector('turbo-frame[loading="lazy"]')
        if (frame) frame.loading = "eager"
    }

    _setPause(reason, on) {
        if (on) this.pauses.add(reason)
        else this.pauses.delete(reason)
        this._syncAutoplay()
    }

    _syncAutoplay() {
        if (!this.hasAutoplayTarget) return

        this.autoplayTarget.classList.toggle("is-paused", document.hidden || this.pauses.size > 0)
        this.autoplayTarget.setAttribute("aria-label", this.pauses.has("manual") ? "Resume autoplay" : "Pause autoplay")
    }

    // Someone who just pressed > wants a full look at the card they asked for,
    // not the tail end of a lap that was already three-quarters round. Rewound
    // through the Web Animations API rather than the usual clear-and-reflow
    // trick: the sweep is an SVG <circle>, and SVG elements have no offsetWidth
    // to read, so that trick has nothing to force the reflow with and silently
    // leaves the lap where it was. Rewinding also works while the animation is
    // paused, which it is for as long as the pointer rests on the strip.
    _restartSweep() {
        if (!this.hasSweepTarget) return

        this.sweepTarget.getAnimations().forEach((animation) => { animation.currentTime = 0 })
    }

    // Height of the card on screen, applied to the track. The off-screen cards
    // overflow it and the viewport clips them, so only the active one sets the
    // section's height. An un-paged desktop never gets a height — its grid sizes
    // itself — but a paged one is clipped just like a phone and needs it.
    // Height the track to the TALLEST card, not the one on screen.
    //
    // Sizing to the active card meant the strip resized on every page, and
    // everything below it moved with it — most visibly the Jane input, which
    // would jump under the cursor as a taller widget slid in. A carousel that
    // shoves the page around is worse than one with some slack under a short
    // card, and autoplay makes it worse again: the jump happens unprompted.
    //
    // Cards are absolutely side by side in the track, so the natural height is
    // the tallest one anyway; this just stops the per-card override undoing it.
    _fit() {
        if (!this.fitHeightValue || !this.hasTrackTarget) return

        const cards = this._cards()
        if (!this._isPaged() || cards.length === 0) {
            this.trackTarget.style.removeProperty("height")
            return
        }
        const tallest = Math.max(...cards.map((card) => card.offsetHeight))
        if (tallest > 0) this.trackTarget.style.height = `${tallest}px`
    }
}
